'use client';
import React, { useRef } from 'react';

const projects = [
  {
    image: "/images/portfolio/portfolio-1.jpg",
    title: "Premium Spirits Labels",
    industryTag: "Liquor",
    labelType: "Metallic Foil",
    description: "High-end metallic foil labels designed for premium glass bottles, ensuring shelf-stopping appeal and durability."
  },
  {
    image: "/images/industries/oil.png",
    title: "Auto Lubricant Labels",
    industryTag: "Automotive",
    labelType: "Peel & Seal",
    description: "Durable, oil-resistant labels with multi-page peel-and-seal functionality for comprehensive product information."
  },
  {
    image: "/images/industries/cosmetics.png",
    title: "Eco-Friendly Cosmetic Wraps",
    industryTag: "Cosmetics",
    labelType: "Sustainable Material",
    description: "Soft-touch, moisture-resistant labels crafted from eco-friendly materials for luxury skincare products."
  },
  {
    image: "/images/industries/food_beverage.png",
    title: "Artisanal Beverage Labels",
    industryTag: "Food & Beverage",
    labelType: "Textured Paper",
    description: "Elegantly textured paper labels for craft beverages, featuring high-resolution graphics and tactile finishes."
  },
  {
    image: "/images/portfolio/portfolio-5.jpg",
    title: "Pharmaceutical Grade Labels",
    industryTag: "Healthcare",
    labelType: "Tamper Evident",
    description: "Precision-printed pharmaceutical labels with integrated tamper-evident features and high-contrast clinical data."
  },
  {
    image: "/images/portfolio/portfolio-6.jpg",
    title: "Sustainable Dairy Packaging",
    industryTag: "Dairy",
    labelType: "Clear-on-Clear",
    description: "Transparent 'no-label' look for dairy products, utilizing moisture-proof adhesives and high-gloss finishes."
  }
];

const ProjectShowcase = () => {
  const projectsContainerRef = useRef(null);

  const scroll = (direction) => {
    if (projectsContainerRef.current) {
      const cardWidth = projectsContainerRef.current.querySelector('div[class*="flex-col bg-white rounded-xl"]')?.offsetWidth || 380;
      const gap = 32; // 8 * 4 = 32px for gap-8
      const scrollAmount = cardWidth + gap;

      projectsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };
  return (
    <div className="w-full bg-[#F1F3F6] py-16 md:py-24">
      <div className="max-w-[1216px] mx-auto px-6 md:px-8">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div className="flex flex-col gap-4 max-w-3xl">
              <h2 className="text-3xl md:text-[40px] font-bold text-[#112140]">Still Deciding? See How We Deliver.</h2>
              <p className="text-lg text-[#475467]">
                Explore real projects and see the precision, quality, and impact of our custom labels — crafted across industries just like yours.
              </p>
            </div>
            <div className="flex gap-[24px] mt-4 sm:mt-0">
              <button
                onClick={() => scroll('left')}
                className="w-[48px] h-[48px] border-2 border-[#2E8B8D] rounded-full shadow-[0px_0px_2px_rgba(0,0,0,0.07),0px_1px_3px_rgba(0,0,0,0.1)] p-[12px] hover:bg-gray-100 transition-colors duration-200"
              >
                <img
                  src="/images/img_icon_iconoir.svg"
                  alt="Previous"
                  className="w-full h-full"
                />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-[48px] h-[48px] border-2 border-[#2E8B8D] rounded-full shadow-[0px_0px_2px_rgba(0,0,0,0.07),0px_1px_3px_rgba(0,0,0,0.1)] p-[12px] hover:bg-gray-100 transition-colors duration-200"
              >
                <img
                  src="/images/img_icon_jam_icons.svg"
                  alt="Next"
                  className="w-full h-full"
                />
              </button>
            </div>
          </div>

          <div
            ref={projectsContainerRef}
            className="w-full flex overflow-x-auto scroll-smooth snap-x snap-mandatory gap-8 mt-4 pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {projects.map((project, index) => (
              <div key={index} className="flex-shrink-0 w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)] flex flex-col bg-white rounded-xl shadow-sm border border-[#E4E7EC] overflow-hidden snap-start">
                <div className="w-full h-[300px] overflow-hidden bg-white flex items-center justify-center">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-contain transform transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="flex flex-col gap-3 p-5">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs font-medium text-[#112140] bg-[#E0F2FE] px-2 py-1 rounded">{project.industryTag}</span>
                    <span className="text-xs font-medium text-[#112140] bg-[#E0F2FE] px-2 py-1 rounded">{project.labelType}</span>
                  </div>
                  <h3 className="text-[18px] font-semibold text-[#112140] mt-1">{project.title}</h3>
                  <p className="text-[14px] text-[#475467] mb-2">{project.description}</p>
                  <a href="#" className="text-[#2E90FA] text-[14px] font-medium flex items-center mt-auto">
                    Know More
                    <svg className="ml-1 w-4 h-4" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.16602 10H15.8327" stroke="#2E90FA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.834 5L15.834 10L10.834 15" stroke="#2E90FA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-16 mb-28 py-8 border-t border-b border-[#E4E7EC]">
        <div className="max-w-[1216px] mx-auto px-6 md:px-8">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-8">
            <img src="/images/img_client_logo_blue_a200.svg" alt="Airbnb" className="h-8" />
            <img src="/images/img_client_logo_blue_gray_900_01.svg" alt="HubSpot" className="h-8" />
            <img src="/images/img_client_logo.svg" alt="Google" className="h-8" />
            <img src="/images/img_client_logo_gray_600.svg" alt="Microsoft" className="h-8" />
            <img src="/images/img_client_logo_deep_orange_300.svg" alt="Walmart" className="h-8" />
            <img src="/images/img_client_logo_purple_800.svg" alt="FedEx" className="h-8" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectShowcase;
